import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Briefcase, Sparkles, Loader2, AlertTriangle, CheckCircle2,
  Users, Wrench, Calendar, MessageSquare, Target, ChevronDown,
  ChevronRight, Plus, Trash2, FileText, TrendingUp, X,
} from "lucide-react";
import CCBSkillHint from "@/components/CCBSkillHint";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface DayToDay { task: string; frequency: string; }
interface Stakeholder { role: string; interaction: string; }
interface Skill { name: string; level: string; category: string; }

interface JobReport {
  role_summary: string;
  scope: "focused" | "mixed" | "department-wide";
  scope_explanation: string;
  focus_areas: string[];
  day_to_day: DayToDay[];
  stakeholders: Stakeholder[];
  skills: Skill[];
  domains: string[];
  complexity: "low" | "medium" | "high";
  complexity_reason: string;
  green_flags: string[];
  red_flags: string[];
  interview_questions: string[];
}

interface SavedJob {
  id: string;
  title: string;
  text: string;
  report: JobReport | null;
  savedAt: string;
}

interface Props { projectId: string; }

// ── localStorage store ──────────────────────────────────────────────────────
function storeKey(projectId: string) { return `job-intel:${projectId}`; }
function loadJobs(projectId: string): SavedJob[] {
  try { return JSON.parse(localStorage.getItem(storeKey(projectId)) || "[]"); } catch { return []; }
}
function saveJobs(projectId: string, jobs: SavedJob[]) {
  localStorage.setItem(storeKey(projectId), JSON.stringify(jobs));
}

// ── Constants ────────────────────────────────────────────────────────────────
const SCOPE_COLOR: Record<string, string> = {
  focused: "text-emerald-600 bg-emerald-50 border-emerald-200",
  mixed: "text-amber-600 bg-amber-50 border-amber-200",
  "department-wide": "text-red-600 bg-red-50 border-red-200",
};
const SCOPE_LABEL: Record<string, string> = {
  focused: "Focused Role", mixed: "Mixed Scope", "department-wide": "Department-Wide",
};
const COMPLEXITY_COLOR: Record<string, string> = {
  low: "text-emerald-600", medium: "text-amber-600", high: "text-red-600",
};
const FREQ_COLOR: Record<string, string> = {
  daily: "bg-indigo-100 text-indigo-700",
  weekly: "bg-sky-100 text-sky-700",
  ongoing: "bg-violet-100 text-violet-700",
  "as-needed": "bg-slate-100 text-slate-600",
};

function Section({ title, icon: Icon, children, defaultOpen = true }: {
  title: string; icon: React.ComponentType<any>; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-md bg-card overflow-hidden">
    <CCBSkillHint tab="job-intelligence" className="mb-4" />
      <button
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-secondary/30 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-accent" />
          <span className="text-xs font-mono font-semibold uppercase tracking-wider">{title}</span>
        </div>
        {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );
}

// ── Cross-Job Patterns ────────────────────────────────────────────────────────
function PatternsPanel({ jobs }: { jobs: SavedJob[] }) {
  const analyzed = jobs.filter((j) => j.report);
  if (analyzed.length === 0) return (
    <p className="text-[11px] text-muted-foreground text-center pt-8">Analyze at least one job to see patterns.</p>
  );

  const skillCount: Record<string, number> = {};
  const domainCount: Record<string, number> = {};
  const scopeCount: Record<string, number> = {};
  const complexityCount: Record<string, number> = {};

  analyzed.forEach(({ report: r }) => {
    if (!r) return;
    (r.skills || []).forEach((s) => { skillCount[s.name] = (skillCount[s.name] || 0) + 1; });
    (r.domains || []).forEach((d) => { domainCount[d] = (domainCount[d] || 0) + 1; });
    scopeCount[r.scope] = (scopeCount[r.scope] || 0) + 1;
    complexityCount[r.complexity] = (complexityCount[r.complexity] || 0) + 1;
  });

  const topSkills = Object.entries(skillCount).sort((a, b) => b[1] - a[1]).slice(0, 10);
  const topDomains = Object.entries(domainCount).sort((a, b) => b[1] - a[1]).slice(0, 6);

  return (
    <div className="space-y-4 text-[11px]">
      <p className="text-muted-foreground">Across <span className="font-semibold text-foreground">{analyzed.length}</span> analyzed job{analyzed.length !== 1 ? "s" : ""}.</p>

      <div>
        <p className="font-mono uppercase tracking-wider text-muted-foreground/70 mb-2">Top Skills</p>
        <div className="space-y-1.5">
          {topSkills.map(([name, count]) => (
            <div key={name} className="flex items-center gap-2">
              <div className="flex-1 text-foreground truncate">{name}</div>
              <div className="w-16 bg-muted rounded-full h-1.5 shrink-0">
                <div className="bg-primary h-1.5 rounded-full" style={{ width: `${(count / analyzed.length) * 100}%` }} />
              </div>
              <span className="text-muted-foreground w-4 text-right">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {topDomains.length > 0 && (
        <div>
          <p className="font-mono uppercase tracking-wider text-muted-foreground/70 mb-2">Domains</p>
          <div className="flex flex-wrap gap-1.5">
            {topDomains.map(([d, c]) => (
              <span key={d} className="border border-border rounded px-2 py-0.5 text-foreground">
                {d} <span className="text-muted-foreground">×{c}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="font-mono uppercase tracking-wider text-muted-foreground/70 mb-2">Scope Mix</p>
        <div className="space-y-1">
          {Object.entries(scopeCount).map(([s, c]) => (
            <div key={s} className="flex items-center justify-between">
              <span className="text-foreground capitalize">{s.replace("-", " ")}</span>
              <span className="text-muted-foreground">{c} / {analyzed.length}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Skills Gap ────────────────────────────────────────────────────────────────
const GAP_KEY = "job-intel:my-skills";

function SkillsGapPanel({ report }: { report: JobReport | null }) {
  const [mySkills, setMySkills] = useState<string[]>([]);
  const [input, setInput] = useState("");

  useEffect(() => {
    try { setMySkills(JSON.parse(localStorage.getItem(GAP_KEY) || "[]")); } catch { setMySkills([]); }
  }, []);

  function addSkill() {
    const s = input.trim();
    if (!s || mySkills.includes(s)) return;
    const updated = [...mySkills, s];
    setMySkills(updated);
    localStorage.setItem(GAP_KEY, JSON.stringify(updated));
    setInput("");
  }

  function removeSkill(s: string) {
    const updated = mySkills.filter((x) => x !== s);
    setMySkills(updated);
    localStorage.setItem(GAP_KEY, JSON.stringify(updated));
  }

  const mySkillsLower = mySkills.map((s) => s.toLowerCase());
  const required = (report?.skills || []).filter((s) => s.level === "required");
  const niceToHave = (report?.skills || []).filter((s) => s.level === "nice-to-have");

  return (
    <div className="space-y-4 text-[11px]">
      {/* My skills input */}
      <div>
        <p className="font-mono uppercase tracking-wider text-muted-foreground/70 mb-2">My Skills</p>
        <div className="flex gap-1 mb-2">
          <input
            className="flex-1 bg-background border border-border rounded px-2 py-1 text-[11px] focus:outline-none focus:border-accent/50 placeholder:text-muted-foreground/40"
            placeholder="Add a skill…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addSkill()}
          />
          <button onClick={addSkill} className="px-2 py-1 border border-border rounded hover:border-accent/50 text-muted-foreground hover:text-accent transition-colors">
            <Plus className="h-3 w-3" />
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {mySkills.map((s) => (
            <span key={s} className="flex items-center gap-1 border border-border rounded px-1.5 py-0.5 text-foreground">
              {s}
              <button onClick={() => removeSkill(s)} className="text-muted-foreground hover:text-destructive transition-colors">
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      </div>

      {/* Gap analysis */}
      {!report && <p className="text-muted-foreground text-center pt-4">Analyze a job to see your gap.</p>}
      {report && (
        <>
          {required.length > 0 && (
            <div>
              <p className="font-mono uppercase tracking-wider text-muted-foreground/70 mb-2">Required</p>
              <div className="space-y-1">
                {required.map((s) => {
                  const have = mySkillsLower.includes(s.name.toLowerCase());
                  return (
                    <div key={s.name} className="flex items-center gap-2">
                      {have
                        ? <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                        : <AlertTriangle className="h-3 w-3 text-destructive shrink-0" />}
                      <span className={have ? "text-foreground" : "text-destructive"}>{s.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {niceToHave.length > 0 && (
            <div>
              <p className="font-mono uppercase tracking-wider text-muted-foreground/70 mb-2">Nice to Have</p>
              <div className="space-y-1">
                {niceToHave.map((s) => {
                  const have = mySkillsLower.includes(s.name.toLowerCase());
                  return (
                    <div key={s.name} className="flex items-center gap-2">
                      {have
                        ? <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                        : <span className="h-3 w-3 shrink-0 text-center text-muted-foreground">○</span>}
                      <span className={have ? "text-foreground" : "text-muted-foreground"}>{s.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function JobIntelligencePanel({ projectId }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [jobs, setJobs] = useState<SavedJob[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [rightPanel, setRightPanel] = useState<"patterns" | "gap">("patterns");

  // form state for current (new or loaded) job
  const [jobTitle, setJobTitle] = useState("");
  const [jobText, setJobText] = useState("");
  const [report, setReport] = useState<JobReport | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const stored = loadJobs(projectId);
    setJobs(stored);
    if (stored.length > 0) loadJob(stored[0]);
  }, [projectId]); // eslint-disable-line

  function loadJob(job: SavedJob) {
    setActiveId(job.id);
    setJobTitle(job.title);
    setJobText(job.text);
    setReport(job.report);
    setError(null);
  }

  function handleNew() {
    setActiveId(null);
    setJobTitle("");
    setJobText("");
    setReport(null);
    setError(null);
  }

  function handleSave() {
    if (!jobTitle.trim() || !jobText.trim()) return;
    const updated = activeId
      ? jobs.map((j) => j.id === activeId ? { ...j, title: jobTitle, text: jobText, report } : j)
      : [...jobs, { id: crypto.randomUUID(), title: jobTitle.trim(), text: jobText.trim(), report, savedAt: new Date().toISOString() }];
    setJobs(updated);
    saveJobs(projectId, updated);
    if (!activeId) {
      const newest = updated[updated.length - 1];
      setActiveId(newest.id);
    }
  }

  function handleDelete(id: string) {
    const updated = jobs.filter((j) => j.id !== id);
    setJobs(updated);
    saveJobs(projectId, updated);
    if (activeId === id) {
      if (updated.length > 0) loadJob(updated[0]);
      else handleNew();
    }
  }

  const handleAnalyze = async () => {
    if (!jobText.trim()) return;
    setAnalyzing(true); setError(null);
    try {
      const res = await fetch(`${BASE}/api/tools/job-intelligence`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ job_text: jobText, project_id: null }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const newReport = data as JobReport;
      setReport(newReport);
      // auto-save the report
      const updated = activeId
        ? jobs.map((j) => j.id === activeId ? { ...j, report: newReport, title: jobTitle || j.title, text: jobText } : j)
        : (() => {
            const title = jobTitle.trim() || "Untitled Job";
            const newJob: SavedJob = { id: crypto.randomUUID(), title, text: jobText, report: newReport, savedAt: new Date().toISOString() };
            return [...jobs, newJob];
          })();
      setJobs(updated);
      saveJobs(projectId, updated);
      if (!activeId) setActiveId((updated[updated.length - 1]).id);
    } catch (err: any) {
      setError(err.message || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="h-full flex min-h-0 overflow-hidden">
      {/* Left sidebar */}
      <div className="w-52 shrink-0 border-r border-border flex flex-col overflow-hidden">
        <div className="p-3 border-b border-border flex items-center justify-between">
          <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Jobs</p>
          <button onClick={handleNew} className="p-1 rounded hover:bg-secondary/50 text-muted-foreground hover:text-accent transition-colors" title="New">
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {jobs.length === 0 && (
            <p className="text-[11px] text-muted-foreground p-3 text-center">No jobs saved yet</p>
          )}
          {jobs.map((job) => (
            <div
              key={job.id}
              onClick={() => loadJob(job)}
              className={`group flex items-start gap-1.5 px-3 py-2 cursor-pointer hover:bg-secondary/40 transition-colors border-b border-border/50 ${activeId === job.id ? "bg-secondary/60" : ""}`}
            >
              <FileText className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-card-foreground truncate">{job.title}</p>
                {job.report && (
                  <span className={`text-[10px] font-medium ${COMPLEXITY_COLOR[job.report.complexity] || ""}`}>
                    {SCOPE_LABEL[job.report.scope] || job.report.scope}
                  </span>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(job.id); }}
                className="opacity-0 group-hover:opacity-100 p-0.5 text-muted-foreground hover:text-destructive transition-all shrink-0"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 mb-1">
            <Briefcase className="h-3 w-3" /> Job Intelligence
          </p>
          <p className="text-xs text-muted-foreground">
            Paste a job posting to understand what you'd actually be doing — scope, day-to-day tasks, stakeholders, and what to watch for.
          </p>
        </div>

        {/* Input form */}
        <div className="space-y-2">
          <input
            className="w-full bg-background border border-border rounded-md px-3 py-1.5 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent/50"
            placeholder="Job title (e.g. Senior Business Analyst – Healthcare)"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
          />
          <textarea
            className="w-full min-h-[180px] bg-background border border-border rounded-md px-3 py-2 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent/50 resize-y"
            placeholder="Paste the full job description here…"
            value={jobText}
            onChange={(e) => setJobText(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={!jobTitle.trim() || !jobText.trim()}
              className="flex items-center gap-1.5 border border-border rounded px-3 py-1.5 text-xs font-medium hover:border-accent/50 hover:text-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Save
            </button>
            <button
              onClick={handleAnalyze}
              disabled={!jobText.trim() || analyzing}
              className="flex items-center gap-1.5 bg-accent text-accent-foreground rounded px-3 py-1.5 text-xs font-semibold hover:bg-accent/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {analyzing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {analyzing ? "Analyzing…" : "Analyze"}
            </button>
          </div>
          {error && <p className="text-[11px] text-destructive">{error}</p>}
        </div>

        {/* Results */}
        {report && (
          <div className="space-y-3">
            <div className="border border-border rounded-md bg-card p-4 space-y-3">
              <p className="text-sm text-card-foreground leading-relaxed">{report.role_summary}</p>
              <div className="flex flex-wrap gap-2">
                <span className={`text-[11px] font-semibold border rounded px-2 py-0.5 ${SCOPE_COLOR[report.scope] || ""}`}>
                  {SCOPE_LABEL[report.scope] || report.scope}
                </span>
                <span className={`text-[11px] font-semibold ${COMPLEXITY_COLOR[report.complexity] || ""}`}>
                  {report.complexity?.charAt(0).toUpperCase() + report.complexity?.slice(1)} complexity
                </span>
                {(report.domains || []).map((d) => (
                  <span key={d} className="text-[10px] border border-border rounded px-1.5 py-0.5 text-muted-foreground">{d}</span>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground italic">{report.scope_explanation}</p>
            </div>

            <Section title="What You'd Actually Do" icon={Calendar}>
              <div className="space-y-1.5 mt-1">
                {(report.day_to_day || []).map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className={`text-[10px] font-medium rounded px-1.5 py-0.5 shrink-0 mt-0.5 ${FREQ_COLOR[item.frequency] || FREQ_COLOR["as-needed"]}`}>
                      {item.frequency}
                    </span>
                    <p className="text-xs text-card-foreground">{item.task}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Who You'd Work With" icon={Users}>
              <div className="space-y-2 mt-1">
                {(report.stakeholders || []).map((s, i) => (
                  <div key={i} className="border border-border rounded px-3 py-2 bg-background">
                    <p className="text-xs font-semibold text-card-foreground">{s.role}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{s.interaction}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Skills Required" icon={Wrench}>
              <div className="mt-2 space-y-2">
                {["technical", "process", "soft"].map((cat) => {
                  const catSkills = (report.skills || []).filter((s) => s.category === cat);
                  if (!catSkills.length) return null;
                  return (
                    <div key={cat}>
                      <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1">{cat}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {catSkills.map((s, i) => (
                          <span key={i} className={`text-[11px] border rounded px-2 py-0.5 ${s.level === "required" ? "border-accent/50 text-accent bg-accent/5" : "border-border text-muted-foreground"}`}>
                            {s.name}{s.level === "nice-to-have" && <span className="ml-1 opacity-50">○</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section title="Signals" icon={Target}>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-mono text-emerald-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Green Flags
                  </p>
                  <ul className="space-y-1">
                    {(report.green_flags || []).map((f, i) => (
                      <li key={i} className="text-[11px] text-card-foreground flex items-start gap-1.5">
                        <span className="text-emerald-500 mt-0.5">✓</span>{f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-[10px] font-mono text-destructive uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> Red Flags
                  </p>
                  <ul className="space-y-1">
                    {(report.red_flags || []).map((f, i) => (
                      <li key={i} className="text-[11px] text-card-foreground flex items-start gap-1.5">
                        <span className="text-destructive mt-0.5">!</span>{f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Section>

            <Section title="Questions to Ask in the Interview" icon={MessageSquare} defaultOpen={false}>
              <ul className="mt-1 space-y-2">
                {(report.interview_questions || []).map((q, i) => (
                  <li key={i} className="text-xs text-card-foreground flex items-start gap-2">
                    <span className="text-accent font-mono font-bold shrink-0">{i + 1}.</span>{q}
                  </li>
                ))}
              </ul>
            </Section>
          </div>
        )}
      </div>

      {/* Right panel: Patterns + Skills Gap */}
      <div className="w-60 shrink-0 border-l border-border flex flex-col overflow-hidden">
        {/* Toggle */}
        <div className="flex border-b border-border shrink-0">
          {(["patterns", "gap"] as const).map((v) => (
            <button
              key={v}
              onClick={() => setRightPanel(v)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium transition-colors ${
                rightPanel === v
                  ? "border-b-2 border-primary text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {v === "patterns" ? <TrendingUp className="h-3 w-3" /> : <Wrench className="h-3 w-3" />}
              {v === "patterns" ? "Patterns" : "Skills Gap"}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {rightPanel === "patterns"
            ? <PatternsPanel jobs={jobs} />
            : <SkillsGapPanel report={report} />}
        </div>
      </div>
    </div>
  );
}
